import React, { useState } from 'react';
import { getCurrency } from "../config/utils";
import { dateIso2Us } from "../config/dateUtils";
import { useSxStyles } from "../styles/sxStyles";
import { getCaseLabel, getCitationLabel } from "./lookupFunctions";
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Grid2 from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { NumericFormat } from "react-number-format";
import Tooltip from '@mui/material/Tooltip'
import OptionList from './OptionList';
import BondList from "./BondList";

const BalanceList = (props) => {
    const balances = props.balances;
    const court = props.court;
    const selectBalance = props.selectBalance;
    const nonCourt = court.nonCourtOrganization;
    const classes = useSxStyles;
    const [payIneligible, setPayIneligible] = useState(false);

    return (
        <>
            <Typography variant='h6' color='primary' sx={{fontSize: '1.2rem', marginTop: '.5rem', marginBottom: '.5rem'}}>
                {balances.length > 0 ? `Balances for ${balances[0].party.partyName}` : 'No balances found'}
            </Typography>
            { balances.length > 0 &&
                <Paper elevation={3} sx={{...classes.listContainer, maxHeight: '65vh', overflow: 'auto'}}>
                    <List sx={classes.list} >
                        {balances && balances.length > 0 && balances.map((balance, idx) => {
                            return ( !balance.selected ?
                                    <>
                                        <ListItem alignItems='flex-start' sx={{...classes.listPadding, marginBottom: '.3rem'}}>
                                            <ListItemText
                                                sx={classes.listText}
                                                primary={
                                                    <Grid2
                                                        container
                                                        spacing={2}
                                                        alignItems="top"
                                                        style={{width: '100%'}}
                                                    >
                                                        { balance.paymentPlanId &&
                                                            <Grid2 size={12} sx={{paddingLeft: '1rem'}}>
                                                                {`Included in payment plan #${balance.paymentPlanId}`}
                                                            </Grid2>
                                                        }
                                                        <Grid2 size={{ xs: 3, lg: 2, xl: 1 }}>
                                                            { (!balance.ineligibleForPayment || payIneligible) ?
                                                                <Tooltip title='Add balance to the payment list' arrow>
                                                                    <Button variant='contained' color='success' onClick={() => selectBalance(balance)} sx={classes.tinyButton}>
                                                                        Pay
                                                                    </Button>
                                                                </Tooltip> :
                                                                <Typography variant='subtitle2' component='span'>Not Payable</Typography>
                                                            }
                                                        </Grid2>
                                                        <Grid2 size={{ xs: 5, lg: 5, xl: 3 }} sx={{paddingLeft: '.2rem'}}>
                                                            {`${getCaseLabel(court)}: ${balance.caseNumber}`}
                                                        </Grid2>
                                                        <Grid2 size={{ xs: 4, lg: 4, xl: 3 }}>
                                                            {balance.citationNumber ? `${getCitationLabel(court)}: ${balance.citationNumber}` : ' '}
                                                        </Grid2>
                                                        <Grid2 size={{ xs: 9, lg: 10, xl: 5 }} sx={{paddingTop: 0}}>
                                                            {balance.description}
                                                        </Grid2>
                                                    </Grid2>
                                                }
                                                slotProps={{
                                                    primaryTypography: {
                                                        component: 'span',
                                                        variant: 'subtitle2',
                                                        sx: classes.inline,
                                                        color: 'textPrimary'
                                                    },
                                                    secondaryTypography: {
                                                        component: 'span',
                                                        variant: 'body2',
                                                        sx: classes.inline,
                                                        color: 'textPrimary'
                                                    }
                                                }}
                                                secondary={
                                                    <Grid2
                                                        container
                                                        spacing={2}
                                                        alignItems="top"
                                                        sx={{ marginTop: '.5rem' }}
                                                        style={{width: '100%'}}
                                                    >
                                                        <Grid2 size={{ xs: 4, lg: 3, xl: 2 }} sx={{paddingTop: 0}}>
                                                            {`Amount: ${getCurrency(balance.courtAmount)}`}
                                                        </Grid2>
                                                        <Grid2 size={{ xs: balance.disposed ? 8 : 5, lg: balance.disposed ? 9 : 6, xl: 3 }} sx={{paddingTop: 0}}>
                                                            {balance.violationDate ? `${nonCourt ? 'Statement' : 'Violation'} Date: ${dateIso2Us(balance.violationDate)}`: ' '}
                                                        </Grid2>
                                                        { !balance.disposed &&
                                                            <Grid2 size={{xs: 3, xl: 1}} sx={{paddingTop: 0}}>
                                                                Pending
                                                            </Grid2>
                                                        }
                                                        { balance.balanceDisplayMessage &&
                                                            <Grid2 size={{xs: 12, xl: 6}} sx={{paddingTop: 0}}>
                                                                <Typography color='secondary'>
                                                                    {balance.balanceDisplayMessage}
                                                                </Typography>
                                                            </Grid2>
                                                        }
                                                        { !balance.ineligibleForPayment && court.acceptPartials && !balance.noPartialPayment && balance.disposed &&
                                                            <>
                                                                <Grid2 size={{ xs: 4, lg: 3, xl: 2 }} sx={{paddingTop: 0}}>
                                                                    <Typography variant='body2'>Partial Payment Amt.</Typography>
                                                                </Grid2>
                                                                <Grid2 size={{ xs: 4, lg: 3, xl: 2 }} sx={{paddingTop: 0}}>
                                                                    <Tooltip title='Enter a partial payment amount' arrow>
                                                                        <FormControl fullWidth >
                                                                            <NumericFormat
                                                                                id='partialAmt'
                                                                                name='partialAmt'
                                                                                customInput={TextField}
                                                                                InputProps={{style: {fontSize: '.9rem', height: '1.6rem'}}}
                                                                                type='text'
                                                                                autoComplete='off'
                                                                                variant='outlined'
                                                                                sx={{marginTop: 0}}
                                                                                decimalScale={2}
                                                                                decimalSeparator='.'
                                                                                fixedDecimalScale={true}
                                                                                onValueChange={values => {balance.partialAmt = values.floatValue ? values.floatValue : 0.00}}
                                                                            />
                                                                        </FormControl>
                                                                    </Tooltip>
                                                                </Grid2>
                                                            </>
                                                        }
                                                        { !balance.ineligibleForPayment && balance.trafficSchoolOptions && balance.trafficSchoolOptions.length > 0 &&
                                                            <OptionList options={balance.trafficSchoolOptions} balance={balance} />
                                                        }
                                                        { (court.bondOnlyBalances || !balance.ineligibleForPayment) && balance.bonds && balance.bonds.length > 0 &&
                                                            <BondList bonds={balance.bonds} balance={balance} setPayIneligible={setPayIneligible} />
                                                        }
                                                    </Grid2>
                                                }
                                            />
                                        </ListItem>
                                        { idx < (balances.length - 1) &&
                                            <Divider sx={classes.listDivider}/>
                                        }
                                    </> : null
                            );
                        })}
                    </List>
                </Paper>
            }
        </>
    );
}

export default BalanceList;
